import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CATALOGO } from '../data/catalogo';
import type { Clase, Preferencias, Recurso, Respaldo } from '../types';
import { CLAVES, almacenamientoDisponible, borrar, escribir, leer } from '../utils/almacenamiento';
import { normalizarClase } from '../utils/clase';
interface Notificacion {
  id: number;
  texto: string;
  tipo: 'ok' | 'error';
}
interface DatosContextValor {
  cargando: boolean;
  almacenamientoOk: boolean;
  aceptado: boolean;
  aceptar: () => void;
  clases: Clase[];
  guardarClase: (clase: Clase) => Clase;
  eliminarClase: (id: string) => void;
  obtenerClase: (id: string) => Clase | undefined;
  personalizados: Recurso[];
  guardarPersonalizado: (recurso: Recurso) => void;
  eliminarPersonalizado: (id: string) => void;
  recursos: Recurso[];
  favoritos: string[];
  alternarFavorito: (id: string) => void;
  recientes: string[];
  registrarReciente: (id: string) => void;
  borrador: Clase | null;
  guardarBorrador: (clase: Clase) => void;
  limpiarBorrador: () => void;
  preferencias: Preferencias;
  guardarPreferencias: (p: Partial<Preferencias>) => void;
  ultimoGuardado: string | null;
  notificaciones: Notificacion[];
  notificar: (texto: string, tipo?: 'ok' | 'error') => void;
  descartarNotificacion: (id: number) => void;
  exportarRespaldo: () => void;
  importarRespaldo: (texto: string, modo: 'combinar' | 'reemplazar') => {
    ok: boolean;
    mensaje: string;
  };
}
const PREFERENCIAS_INICIALES: Preferencias = {
  schemaVersion: 1,
  ultimoNivel: 'Introducción',
  verTodosLosNiveles: false
};
const DatosContext = createContext<DatosContextValor | null>(null);
export function DatosProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cargando, setCargando] = useState(true);
  const [almacenamientoOk] = useState(() => almacenamientoDisponible());
  const [aceptado, setAceptado] = useState(false);
  const [clases, setClases] = useState<Clase[]>([]);
  const [personalizados, setPersonalizados] = useState<Recurso[]>([]);
  const [favoritos, setFavoritos] = useState<string[]>([]);
  const [recientes, setRecientes] = useState<string[]>([]);
  const [borrador, setBorrador] = useState<Clase | null>(null);
  const [preferencias, setPreferencias] = useState<Preferencias>(PREFERENCIAS_INICIALES);
  const [ultimoGuardado, setUltimoGuardado] = useState<string | null>(null);
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const contadorNotificacion = useRef(0);
  useEffect(() => {
    setAceptado(leer<boolean>(CLAVES.aceptacion, false));
    setClases(leer<Clase[]>(CLAVES.clases, []).map(c => normalizarClase(c)));
    setPersonalizados(leer<Recurso[]>(CLAVES.personalizados, []));
    setFavoritos(leer<string[]>(CLAVES.favoritos, []));
    const b = leer<Clase | null>(CLAVES.borrador, null);
    setBorrador(b ? normalizarClase(b) : null);
    setPreferencias(leer<Preferencias>(CLAVES.preferencias, PREFERENCIAS_INICIALES));
    setCargando(false);
  }, []);
  const marcarGuardado = useCallback(() => {
    setUltimoGuardado(new Date().toISOString());
  }, []);
  const notificar = useCallback((texto: string, tipo: 'ok' | 'error' = 'ok') => {
    contadorNotificacion.current += 1;
    const id = contadorNotificacion.current;
    setNotificaciones(prev => [...prev, {
      id,
      texto,
      tipo
    }]);
    window.setTimeout(() => {
      setNotificaciones(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);
  const descartarNotificacion = useCallback((id: number) => {
    setNotificaciones(prev => prev.filter(n => n.id !== id));
  }, []);
  const persistirClases = useCallback((siguientes: Clase[]) => {
    setClases(siguientes);
    escribir(CLAVES.clases, siguientes);
    marcarGuardado();
  }, [marcarGuardado]);
  const aceptar = useCallback(() => {
    setAceptado(true);
    escribir(CLAVES.aceptacion, true);
    marcarGuardado();
  }, [marcarGuardado]);
  const guardarClase = useCallback((clase: Clase) => {
    const actualizada: Clase = {
      ...clase,
      modificadoEn: new Date().toISOString()
    };
    const existe = clases.some(c => c.id === actualizada.id);
    const siguientes = existe ? clases.map(c => c.id === actualizada.id ? actualizada : c) : [actualizada, ...clases];
    persistirClases(siguientes);
    return actualizada;
  }, [clases, persistirClases]);
  const eliminarClase = useCallback((id: string) => {
    persistirClases(clases.filter(c => c.id !== id));
  }, [clases, persistirClases]);
  const obtenerClase = useCallback((id: string) => clases.find(c => c.id === id), [clases]);
  const guardarPersonalizado = useCallback((recurso: Recurso) => {
    const existe = personalizados.some(r => r.id === recurso.id);
    const siguientes = existe ? personalizados.map(r => r.id === recurso.id ? recurso : r) : [recurso, ...personalizados];
    setPersonalizados(siguientes);
    escribir(CLAVES.personalizados, siguientes);
    marcarGuardado();
  }, [personalizados, marcarGuardado]);
  const eliminarPersonalizado = useCallback((id: string) => {
    const siguientes = personalizados.filter(r => r.id !== id);
    setPersonalizados(siguientes);
    escribir(CLAVES.personalizados, siguientes);
    marcarGuardado();
  }, [personalizados, marcarGuardado]);
  const alternarFavorito = useCallback((id: string) => {
    const siguientes = favoritos.includes(id) ? favoritos.filter(f => f !== id) : [...favoritos, id];
    setFavoritos(siguientes);
    escribir(CLAVES.favoritos, siguientes);
    marcarGuardado();
  }, [favoritos, marcarGuardado]);
  const registrarReciente = useCallback((id: string) => {
    setRecientes(prev => [id, ...prev.filter(r => r !== id)].slice(0, 12));
  }, []);
  const guardarBorrador = useCallback((clase: Clase) => {
    const actualizado = {
      ...clase,
      modificadoEn: new Date().toISOString()
    };
    setBorrador(actualizado);
    escribir(CLAVES.borrador, actualizado);
    marcarGuardado();
  }, [marcarGuardado]);
  const limpiarBorrador = useCallback(() => {
    setBorrador(null);
    borrar(CLAVES.borrador);
  }, []);
  const guardarPreferencias = useCallback((p: Partial<Preferencias>) => {
    setPreferencias(prev => {
      const siguiente = {
        ...prev,
        ...p,
        schemaVersion: 1 as const
      };
      escribir(CLAVES.preferencias, siguiente);
      return siguiente;
    });
  }, []);
  const recursos = useMemo(() => [...CATALOGO, ...personalizados], [personalizados]);
  const exportarRespaldo = useCallback(() => {
    const respaldo: Respaldo = {
      schemaVersion: 1,
      exportadoEn: new Date().toISOString(),
      clases,
      ejerciciosPersonalizados: personalizados,
      favoritos,
      preferencias
    };
    const blob = new Blob([JSON.stringify(respaldo, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `planificador-telas-respaldo-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
    notificar('Respaldo exportado.');
  }, [clases, personalizados, favoritos, preferencias, notificar]);
  const importarRespaldo = useCallback((texto: string, modo: 'combinar' | 'reemplazar') => {
    let datos: Respaldo;
    try {
      datos = JSON.parse(texto) as Respaldo;
    } catch {
      return {
        ok: false,
        mensaje: 'El archivo no es un JSON válido.'
      };
    }
    if (!datos || typeof datos !== 'object' || !Array.isArray(datos.clases)) {
      return {
        ok: false,
        mensaje: 'El archivo no tiene el formato esperado: falta la lista de clases.'
      };
    }
    const clasesImportadas = datos.clases.filter(c => c && typeof c === 'object' && typeof c.id === 'string').map(c => normalizarClase(c));
    const personalizadosImportados = Array.isArray(datos.ejerciciosPersonalizados) ? datos.ejerciciosPersonalizados.filter(r => r && typeof r.id === 'string' && typeof r.nombre === 'string') : [];
    const favoritosImportados = Array.isArray(datos.favoritos) ? datos.favoritos.filter(f => typeof f === 'string') : [];
    if (modo === 'reemplazar') {
      persistirClases(clasesImportadas);
      setPersonalizados(personalizadosImportados);
      escribir(CLAVES.personalizados, personalizadosImportados);
      setFavoritos(favoritosImportados);
      escribir(CLAVES.favoritos, favoritosImportados);
      if (datos.preferencias) guardarPreferencias(datos.preferencias);
      return {
        ok: true,
        mensaje: `Se reemplazaron los datos: ${clasesImportadas.length} clases importadas.`
      };
    }
    const idsExistentes = new Set(clases.map(c => c.id));
    const nuevasClases = clasesImportadas.filter(c => !idsExistentes.has(c.id));
    persistirClases([...clases, ...nuevasClases]);
    const idsPers = new Set(personalizados.map(r => r.id));
    const nuevosPers = personalizadosImportados.filter(r => !idsPers.has(r.id));
    const persCombinados = [...personalizados, ...nuevosPers];
    setPersonalizados(persCombinados);
    escribir(CLAVES.personalizados, persCombinados);
    const favCombinados = Array.from(new Set([...favoritos, ...favoritosImportados]));
    setFavoritos(favCombinados);
    escribir(CLAVES.favoritos, favCombinados);
    return {
      ok: true,
      mensaje: `Se combinaron los datos: ${nuevasClases.length} clases nuevas y ${nuevosPers.length} ejercicios personalizados.`
    };
  }, [clases, personalizados, favoritos, persistirClases, guardarPreferencias]);
  const valor: DatosContextValor = {
    cargando,
    almacenamientoOk,
    aceptado,
    aceptar,
    clases,
    guardarClase,
    eliminarClase,
    obtenerClase,
    personalizados,
    guardarPersonalizado,
    eliminarPersonalizado,
    recursos,
    favoritos,
    alternarFavorito,
    recientes,
    registrarReciente,
    borrador,
    guardarBorrador,
    limpiarBorrador,
    preferencias,
    guardarPreferencias,
    ultimoGuardado,
    notificaciones,
    notificar,
    descartarNotificacion,
    exportarRespaldo,
    importarRespaldo
  };
  return <DatosContext.Provider value={valor} data-id="element-434" data-mp="2ecfv739xm59j">{children}</DatosContext.Provider>;
}
export function useDatos(): DatosContextValor {
  const ctx = useContext(DatosContext);
  if (!ctx) throw new Error('useDatos debe usarse dentro de DatosProvider');
  return ctx;
}